import { Category, Product } from '../../types/api';

import { baseApi } from './baseApi';

export interface GetCategoriesParams {
  level?: 'all' | 'top' | 'sub';
  parentId?: string;
}

export interface GetCategoriesResponse {
  status: 'success';
  message: string;
  data: {
    categories: Category[];
  };
}

export interface GetCategoryResponse {
  status: 'success';
  message: string;
  data: {
    category: Category;
  };
}

export interface GetProductsParams {
  categoryId: string;
  page?: number;
  limit?: number;
}

export interface GetProductsResponse {
  status: 'success';
  message: string;
  data: {
    products: Product[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface GetProductResponse {
  status: 'success';
  message: string;
  data: {
    product: Product;
  };
}

export interface HideProductsStatus {
  bot: boolean;
  website: boolean;
}

export interface HideSSNStatus {
  bot: boolean;
  website: boolean;
}

export interface GetHideProductsStatusResponse {
  status: 'success';
  data: {
    hideProducts: HideProductsStatus;
    hideSSN: HideSSNStatus;
    ssnCategoryId: string[];
  };
}

export const catalogApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get Categories
    getCategories: builder.query<GetCategoriesResponse, GetCategoriesParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params.level) searchParams.append('level', params.level);
        if (params.parentId) searchParams.append('parentId', params.parentId);

        const queryString = searchParams.toString();
        return {
          url: `/catalog/categories${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Categories'],
    }),

    // Get Category Details
    getCategory: builder.query<GetCategoryResponse, string>({
      query: (categoryId) => ({
        url: `/catalog/categories/${categoryId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, categoryId) => [{ type: 'Category', id: categoryId }],
    }),

    // Get Products in Category
    getCategoryProducts: builder.query<GetProductsResponse, GetProductsParams>({
      query: ({ categoryId, page = 1, limit = 20 }) => {
        if (!categoryId || categoryId === 'undefined' || categoryId === '') {
          throw new Error('Category ID is required');
        }
        
        const params = new URLSearchParams();
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());

        const queryString = params.toString();
        return {
          url: `/catalog/categories/${categoryId}/products${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, { categoryId }) => [{ type: 'Products', id: categoryId }],
    }),

    // Get Product Details
    getProduct: builder.query<GetProductResponse, string>({
      query: (productId) => ({
        url: `/catalog/products/${productId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, productId) => [{ type: 'Product', id: productId }],
    }),

    // Get Hide Products Status
    getHideProductsStatus: builder.query<GetHideProductsStatusResponse, void>({
      query: () => ({
        url: `/catalog/products/hide-status`,
        method: 'GET',
      }),
      providesTags: ['HideProducts'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useGetCategoryProductsQuery,
  useGetProductQuery,
  useGetHideProductsStatusQuery,
} = catalogApi;

